import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import { PAYOUTS_API_HOSTNAME , ACCOUNTS_API_HOSTNAME } from "../utils/constants";

export interface ApiResponse<T> {
  entity: number;
  data: T;
  error: T;
  authorization?: string;
}

interface ActionResponse<T> {
  type: string;
  payload: T & { message: string };
}

/**
 * Axios default settings
 */
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "x-csrftoken";
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    throw error;
  }
);

// TODO: Update/remove on finalising authorization method
axios.interceptors.request.use((config) => {
  return {
    ...config,
    headers: {
      ...config.headers,
      // Authorization: `Bearer ${Cookies.get('authToken')}`,
    },
  };
});

/**
 * create api path
 * @param url string
 */
const getPath = (url: string): string => `${PAYOUTS_API_HOSTNAME}/${url}`;
const getAccountPath = (url: string): string => `${ACCOUNTS_API_HOSTNAME}/${url}`;

/**
 * api basic operations
 *
 */
const api = {
  get: <T>(
    url: string,
    config: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<ApiResponse<T>>> => axios.get(getPath(url), config),

  post: <T, K>(
    url: string,
    data: K = {} as K,
    config: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<ApiResponse<T>>> =>
    axios.post(getPath(url), data, config),

  postAccounts: <T, K>(
    url: string,
    data: K = {} as K,
    config: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<ApiResponse<T>>> =>
    axios.post(getAccountPath(url), data, config),

  delete: (url: string, config = undefined): Promise<AxiosResponse<any>> =>
    axios.delete(getPath(url), config),

  put: <T>(
    url: string,
    data: any,
    config: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<ApiResponse<T>>> =>
    axios.put(getPath(url), data, config),
};

/**
 *
 * @param response ApiResponse<T>
 * @param successType string
 * @param errorType string
 */
export const handleResponse = <T>(
  response: AxiosResponse<ApiResponse<T>>,
  successType: string,
  errorType: string
): ActionResponse<ApiResponse<T>> => {
  const status = response?.status || 404;
  const message =
    (response && response.statusText) ||
    "We are facing an unexpected error. Please try again.";

  switch (status) {
    case 200:
    case 201:
    case 206:
      return {
        type: successType,
        payload: { ...response.data, message: "Success" },
      };
    case 422:
    case 400:
    case 404:
    case 412:
    case 503: // Service Not available
    case 409: // Conflict or duplicate request
    case 500:
    case 599: {
      const error = response?.data || {
        error: { reason: "unexpected_error", type: "internal_server_error" },
      };
      return {
        type: errorType,
        payload: {
          ...error,
          message: message,
        },
      };
    }
    case 401:
    case 403:
      return {
        type: "AUTHENTICATION_REJECT",
        payload: {
          ...response?.data,
          message: message,
        },
      };

    default:
      return {} as ActionResponse<ApiResponse<T>>;
  }
};

export default api;
